'use client';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Markdown } from '@/components/ui/Markdown';
import { stripKnowledgeBreadcrumbHeadings } from '@/lib/markdown/knowledgeSection';
import { cn } from '@/lib/utils';

/** Shared KB snippet presentation. The entire body goes through the app's safe
 * renderer. The optional preview limits only the rendered box, never source.
 */
export function KnowledgeSectionMarkdown({ content, sectionPath, documentTitle, collapsible = false, interactiveLinks = true, className }: {
  content: string | null | undefined;
  sectionPath?: string | null;
  documentTitle?: string | null;
  collapsible?: boolean;
  interactiveLinks?: boolean;
  className?: string;
}) {
  const body = useMemo(() => stripKnowledgeBreadcrumbHeadings(content ?? '', sectionPath, documentTitle),
    [content, sectionPath, documentTitle]);
  const id = useId();
  const viewport = useRef<HTMLDivElement>(null);
  const rendered = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    if (!collapsible || expanded) return;
    const measure = () => {
      const el = viewport.current;
      if (el) setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (viewport.current) observer?.observe(viewport.current);
    if (rendered.current) observer?.observe(rendered.current);
    return () => observer?.disconnect();
  }, [body, collapsible, expanded]);

  return (
    <div className="min-w-0">
      <div className="relative">
        {/* Reserve 12rem for a collapsed sidebar preview. Expansion restores
            its full height, and the full parsed content remains mounted. */}
        <div id={id} ref={viewport} className={collapsible && !expanded ? 'max-h-48 overflow-hidden' : undefined}
          onFocusCapture={() => { if (collapsible && overflows) setExpanded(true); }}>
          <div ref={rendered}>
            <Markdown content={body} variant="compact" interactiveLinks={interactiveLinks}
              className={cn('app-markdown--section text-sm text-ink-secondary break-words', className)}
            />
          </div>
        </div>
        {collapsible && overflows && !expanded && (
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-surface-raised to-transparent" />
        )}
      </div>
      {collapsible && overflows && (
        <button type="button" aria-controls={id} aria-expanded={expanded}
          onClick={() => setExpanded(open => !open)}
          className="mt-1 rounded-sm px-1 text-xs font-medium text-brand hover:text-brand-hover hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
